import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { DashboardCard } from '../components/DashboardCard'

// Mock expenses data
const mockExpensesData = {
  student: {
    name: 'น้องใส',
    student_code: 'STU-001'
  },
  pending_expenses: [
    {
      id: 'EXP-001',
      date: '2025-08-17',
      description: 'ข้าวกล่อง + น้ำส้ม',
      amount: 55,
      category: 'ค่าอาหาร',
      receipt_url: null,
      status: 'pending_payment' as const,
      due_date: '2025-08-31',
      notes: 'ซื้อจากโรงอาหาร อาหารกลางวัน'
    },
    {
      id: 'EXP-002',
      date: '2025-08-16',
      description: 'ขนม + โค้ก',
      amount: 50,
      category: 'ค่าขนม',
      receipt_url: null,
      status: 'pending_payment' as const,
      due_date: '2025-08-31',
      notes: 'ซื้อจากแม่ค้าหน้าโรงเรียน'
    },
    {
      id: 'EXP-003',
      date: '2025-08-15',
      description: 'ดินสอ 2B + ยางลบ',
      amount: 50,
      category: 'อุปกรณ์การเรียน',
      receipt_url: null,
      status: 'pending_payment' as const,
      due_date: '2025-08-31',
      notes: 'ซื้อเพิ่มเติมเพราะของเดิมหาย'
    }
  ],
  paid_expenses: [
    {
      id: 'EXP-004',
      date: '2025-08-14',
      description: 'ค่าถ่ายเอกสาร',
      amount: 25,
      category: 'อุปกรณ์การเรียน',
      receipt_url: '/receipts/exp-004.jpg',
      status: 'paid' as const,
      paid_date: '2025-08-14',
      payment_reference: 'PAY-EXP-004',
      notes: 'ถ่ายเอกสารแบบฝึกหัดเพิ่มเติม'
    },
    {
      id: 'EXP-005',
      date: '2025-08-12',
      description: 'ข้าวผัด + น้ำเปล่า',
      amount: 60,
      category: 'ค่าอาหาร',
      receipt_url: '/receipts/exp-005.jpg',
      status: 'paid' as const,
      paid_date: '2025-08-12',
      payment_reference: 'PAY-EXP-005',
      notes: 'อาหารกลางวันวันเสาร์'
    }
  ],
  monthly_summary: {
    current_month: {
      month: 'สิงหาคม 2568',
      total_pending: 155,
      total_approved: 85,
      total_paid: 85,
      categories: {
        'ค่าอาหาร': { amount: 115, count: 2 },
        'ค่าขนม': { amount: 50, count: 1 },
        'อุปกรณ์การเรียน': { amount: 75, count: 2 }
      }
    },
    previous_month: {
      month: 'กรกฎาคม 2568',
      total_paid: 220,
      categories: {
        'ค่าอาหาร': { amount: 180, count: 6 },
        'ค่าขนม': { amount: 40, count: 2 }
      }
    }
  }
}

export const ExpensesPage: React.FC = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'pending' | 'paid' | 'summary'>('pending')
  

  // Utility functions
  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }

  const formatThaiBaht = (amount: number) => {
    return `฿${new Intl.NumberFormat('th-TH').format(amount)}`
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'ค่าอาหาร': return '🍽️'
      case 'ค่าขนม': return '🍪'
      case 'อุปกรณ์การเรียน': return '✏️'
      case 'ค่าเดินทาง': return '🚗'
      case 'อื่นๆ': return '📦'
      default: return '💰'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ค่าอาหาร': return 'bg-green-100 text-green-800'
      case 'ค่าขนม': return 'bg-orange-100 text-orange-800'
      case 'อุปกรณ์การเรียน': return 'bg-blue-100 text-blue-800'
      case 'ค่าเดินทาง': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handlePayExpense = (expenseId: string) => {
    navigate(`/payment-slip/${expenseId}`)
  }

  const handleShowInvoice = (expenseId: string) => {
    navigate(`/invoice/${expenseId}`)
  }


  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date()
    const due = new Date(dueDate)
    const diffTime = due.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader 
        title="ค่าใช้จ่าย"
        subtitle={`ค่าใช้จ่ายเพิ่มเติมของ${mockExpensesData.student.name}`}
      />
      
      <div className="px-4 py-6 space-y-6">
        {/* Tab Navigation */}
        <div className="flex bg-white rounded-lg border p-1">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'pending'
                ? 'bg-orange-500 text-white'
                : 'text-gray-600 hover:text-orange-600'
            }`}
          >
            💰 รอชำระ ({mockExpensesData.pending_expenses.length})
          </button>
          <button
            onClick={() => setActiveTab('paid')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'paid'
                ? 'bg-orange-500 text-white'
                : 'text-gray-600 hover:text-orange-600'
            }`}
          >
            ✅ ชำระแล้ว
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'summary'
                ? 'bg-orange-500 text-white'
                : 'text-gray-600 hover:text-orange-600'
            }`}
          >
            📊 สรุป
          </button>
        </div>

        {/* Pending Expenses Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            <DashboardCard 
              title="💰 ค่าใช้จ่ายรอชำระ"
              className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200"
            >
              <div className="space-y-3">
                {mockExpensesData.pending_expenses.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl mb-2 block">🎉</span>
                    <p className="text-gray-600">ไม่มีค่าใช้จ่ายรอชำระ</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-red-900">
                          รวมยอดรอชำระ:
                        </span>
                        <span className="text-lg font-bold text-red-600">
                          {formatThaiBaht(mockExpensesData.pending_expenses.reduce((sum, exp) => sum + exp.amount, 0))}
                        </span>
                      </div>
                    </div>
                    
                    {mockExpensesData.pending_expenses.map((expense) => (
                      <div key={expense.id} className="bg-white rounded-lg border p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">{getCategoryIcon(expense.category)}</span>
                            <div>
                              <h3 className="font-semibold text-gray-900">{expense.description}</h3>
                              <p className="text-sm text-gray-600">{formatThaiDate(expense.date)}</p>
                              <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getCategoryColor(expense.category)}`}>
                                {expense.category}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">
                              {formatThaiBaht(expense.amount)}
                            </p>
                            <div className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium mt-1">
                              รอชำระ
                            </div>
                            {expense.due_date && (
                              <p className="text-xs text-gray-500 mt-1">
                                ครบกำหนด: {formatThaiDate(expense.due_date)}
                                {getDaysUntilDue(expense.due_date) <= 3 && (
                                  <span className="text-red-500 font-medium ml-1">
                                    (เหลือ {getDaysUntilDue(expense.due_date)} วัน)
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {expense.notes && (
                          <div className="bg-gray-50 rounded p-2 mb-3">
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">หมายเหตุ:</span> {expense.notes}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handlePayExpense(expense.id)}
                            className="btn-primary flex-1 text-sm"
                          >
                            💳 จ่าย
                          </button>
                          <button 
                            onClick={() => handleShowInvoice(expense.id)}
                            className="btn-secondary flex-1 text-sm"
                          >
                            📋 ดูใบแจ้งหนี้
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </DashboardCard>

            <DashboardCard 
              title="💳 วิธีการชำระค่าใช้จ่าย"
              className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
            >
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <span className="text-lg">1️⃣</span>
                  <div>
                    <p className="font-medium text-gray-900">ตรวจสอบรายละเอียด</p>
                    <p className="text-gray-600">ตรวจสอบวันที่ รายการ และจำนวนเงินที่ต้องจ่าย</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">2️⃣</span>
                  <div>
                    <p className="font-medium text-gray-900">กดปุ่ม "จ่าย"</p>
                    <p className="text-gray-600">เลือกชำระด้วย QR Code หรือโอนเงิน</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">3️⃣</span>
                  <div>
                    <p className="font-medium text-gray-900">อัพโหลดสลิปการโอน</p>
                    <p className="text-gray-600">ถ่ายรูปสลิปและส่งผ่านระบบ</p>
                  </div>
                </div>
              </div>
            </DashboardCard>
          </div>
        )}

        {/* Paid Expenses Tab */}
        {activeTab === 'paid' && (
          <div className="space-y-4">
            <DashboardCard 
              title="✅ ค่าใช้จ่ายที่ชำระแล้ว"
              className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
            >
              <div className="space-y-3">
                {mockExpensesData.paid_expenses.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl mb-2 block">📝</span>
                    <p className="text-gray-600">ยังไม่มีค่าใช้จ่ายที่ชำระแล้ว</p>
                  </div>
                ) : (
                  mockExpensesData.paid_expenses.map((expense) => (
                    <div key={expense.id} className="bg-white rounded-lg border p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{getCategoryIcon(expense.category)}</span>
                          <div>
                            <h3 className="font-semibold text-gray-900">{expense.description}</h3>
                            <p className="text-sm text-gray-600">{formatThaiDate(expense.date)}</p>
                            <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getCategoryColor(expense.category)}`}>
                              {expense.category}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {formatThaiBaht(expense.amount)}
                          </p>
                          <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium mt-1">
                            ✓ ชำระแล้ว
                          </div>
                        </div>
                      </div>
                      
                      {expense.notes && (
                        <div className="bg-gray-50 rounded p-2 mb-3">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">หมายเหตุ:</span> {expense.notes}
                          </p>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>ชำระเมื่อ: {formatThaiDate(expense.paid_date!)}</div>
                        {expense.payment_reference && (
                          <div>เลขอ้างอิง: {expense.payment_reference}</div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </DashboardCard>
          </div>
        )}

        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="space-y-4">
            <DashboardCard 
              title="📊 สรุปค่าใช้จ่ายประจำเดือน"
              className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
            >
              <div className="space-y-4">
                {/* Current Month Summary */}
                <div className="bg-white rounded-lg border p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {mockExpensesData.monthly_summary.current_month.month}
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-600">รอชำระ</p>
                      <p className="text-lg font-bold text-red-600">
                        {formatThaiBaht(mockExpensesData.monthly_summary.current_month.total_pending)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">ชำระแล้ว</p>
                      <p className="text-lg font-bold text-green-600">
                        {formatThaiBaht(mockExpensesData.monthly_summary.current_month.total_paid)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">แยกตามประเภท:</h4>
                    {Object.entries(mockExpensesData.monthly_summary.current_month.categories).map(([category, data]) => (
                      <div key={category} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getCategoryIcon(category)}</span>
                          <span className="text-sm font-medium">{category}</span>
                          <span className="text-xs text-gray-500">({data.count} รายการ)</span>
                        </div>
                        <span className="font-semibold">{formatThaiBaht(data.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Previous Month Summary */}
                <div className="bg-white rounded-lg border p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {mockExpensesData.monthly_summary.previous_month.month}
                  </h3>
                  
                  <div className="mb-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">รวมยอดที่ชำระ</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatThaiBaht(mockExpensesData.monthly_summary.previous_month.total_paid)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">แยกตามประเภท:</h4>
                    {Object.entries(mockExpensesData.monthly_summary.previous_month.categories).map(([category, data]) => (
                      <div key={category} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getCategoryIcon(category)}</span>
                          <span className="text-sm font-medium">{category}</span>
                          <span className="text-xs text-gray-500">({data.count} รายการ)</span>
                        </div>
                        <span className="font-semibold">{formatThaiBaht(data.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </DashboardCard>
          </div>
        )}
      </div>
      
      <TabNavigation />

    </div>
  )
}